import React, { useState } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import ReactSelect from 'components/theme/Select'
import { FormHelperText } from '@material-ui/core'
import ActionType from '../../actions/ActionType'
import language from 'utils/vast-languagepack'
import {
  contactsWithEmailsSelector,
  groupOptionsSelector,
  contactsWithPhonesSelector,
} from '../../selectors'
import { getAllGroups, getGroup } from 'actions/contacts/groups'
import { getContactsWithEmails } from 'actions/contacts'
import { createSelector } from 'reselect'

const getSelectedNumberUseCase = createSelector(
  [
    state => Object.values(state.data.a2p10dlc || {})?.[0]?.messagingServices || {},
    state => Object.values(state.data.a2p10dlc || {})?.[0]?.phones || {},
    state => state.tasks.addOrEdit.fromNumberSelected.value,
    state => state.data.phonenumbers || {},
  ],
  (messagingServices, phones, selectedNumber, phonenumbers) => {
    const numberObj = Object.values(phonenumbers).find(
      n => n.phone_number === selectedNumber
    )
    if (!numberObj) return null

    const phone = phones[numberObj?.phone_number_sid]
    const serviceSid = phone?.serviceSid
    
    if (!serviceSid) return null
    
    const useCase = Object.entries(messagingServices).find(
      ([_, service]) => service.sid === serviceSid
    )
    
    return useCase ? useCase[0] : null
  }
)

const mapStateToProps = state => {
  const task = state.tasks.tasksByID[state.tasks.addOrEdit.taskID]
  const stats = task?.sms_stats || task?.email_stats
  const campaignStatus =
    state.tasks.addOrEdit.originalCampaignSelected?.campaign?.status
  const automationTypeSelected = state.tasks.addOrEdit.automationTypeSelected
  const disabled = Boolean(
    (stats && automationTypeSelected !== 'stage') ||
      campaignStatus === 'L' ||
      campaignStatus === 'C',
  )

  return {
    value: state.tasks.addOrEdit.select.value,
    error: state.tasks.addOrEdit.select.error
      ? state.tasks.addOrEdit.select.error
      : null,
    contactsWithEmails: contactsWithEmailsSelector(state),
    contactsWithPhones: contactsWithPhonesSelector(state),
    groups: groupOptionsSelector(state),
    selectedActionPrediction:
      state.tasks.addOrEdit.fields.selectedActionPrediction.value,
    automationTypeSelected: state.tasks.addOrEdit.automationTypeSelected,
    disabled,
    groupLoadStatus: state.loadStatus.groups,
    contactLoadStatus: state.loadStatus.contacts,
    selectedNumberUseCase: getSelectedNumberUseCase(state),
    selectedNumber: state.tasks.addOrEdit.fromNumberSelected.value,
  }
}

const mapDispatchToProps = dispatch => {
  const { Tasks } = ActionType
  const dispatchType = (type, payload) => dispatch =>
    dispatch({ type, payload })
  return bindActionCreators(
    {
      handleMultiChange: newValue =>
        dispatchType(Tasks.HANDLE_MULTI_CHANGE, newValue),
      getAllGroups,
      getContactsWithEmails,
      getGroup,
    },
    dispatch,
  )
}

const AddOrEditTaskPage_Select = ({
  value,
  error,
  contactsWithEmails,
  contactsWithPhones,
  groups,
  handleMultiChange,
  selectedActionPrediction,
  automationTypeSelected,
  disabled,
  warning,
  groupLoadStatus,
  getAllGroups,
  phoneOrEmail,
  contactLoadStatus,
  getContactsWithEmails,
  getGroup,
  selectedNumberUseCase,
  selectedNumber,
}) => {
  const [totalContacts, setTotalContacts] = useState(0)

  if (!groupLoadStatus) {
    getAllGroups()
  }

  if (!contactLoadStatus) {
    getContactsWithEmails()
  }

  const customStyles = {
    control: provided => ({
      ...provided,
      borderColor: error
        ? '#f44336'
        : warning
        ? '#d89300'
        : 'rgba(0, 0, 0, 0.23)',
      borderRadius: '28px',
      padding: '9px 0',
    }),
    menu: provided => ({
      ...provided,
      zIndex: 9999,
      position: 'absolute',
      width: '100%',
    }),
    menuPortal: provided => ({
      ...provided,
      zIndex: 9999,
    }),
  }

  let options = groups

  if (phoneOrEmail === 'sms' || selectedActionPrediction === 'sms') {
    options = groups.concat(contactsWithPhones)
  }

  if (phoneOrEmail === 'email' || phoneOrEmail === 'blank' || selectedActionPrediction === 'email') {
    options = groups.concat(contactsWithEmails)
  }

  const handleSelectionChange = async (newValue, actionMeta) => {
    const previousTotal = totalContacts
    let newTotal = previousTotal

    if (actionMeta.option) {
      if (actionMeta.action === 'select-option') {
        newTotal += actionMeta.option.groupID 
          ? actionMeta.option.contact_count 
          : 1
      } else if (actionMeta.action === 'remove-option') {
        newTotal -= actionMeta.option.groupID 
          ? actionMeta.option.contact_count 
          : 1
      }
    }

    if (previousTotal <= 1000 && newTotal > 1000 && 
        selectedNumber && selectedNumberUseCase === 'LOW_VOLUME') {
      const shouldProceed = window.confirm(
        'You are attempting to send to over 1,000 contacts using a LOW VOLUME phone number. ' +
        'It is recommended to apply for a higher volume use case to ensure better delivery rates. ' +
        'Would you like to proceed anyway?'
      )
      
      if (!shouldProceed) {
        return // Don't update the selection if user cancels
      }
    }

    setTotalContacts(newTotal)
    handleMultiChange(newValue)
  }

  return (
    <div
      style={automationTypeSelected === 'stage' ? { display: 'none' } : null}
    >
      <ReactSelect
        styles={customStyles}
        value={value}
        onChange={handleSelectionChange}
        closeMenuOnSelect={false}
        isMulti
        options={options}
        placeholder={language.task.add.helperText.groupName}
        isDisabled={disabled}
        menuPortalTarget={document.body}
      />
      <FormHelperText error={Boolean(error)}>{error}</FormHelperText>
    </div>
  )
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AddOrEditTaskPage_Select)
